import { authHeader } from '../utils/authHeader';

import { API_BASE_URL } from '../config';

// ==================== GALERÍA ====================

export const getSpaceGallery = async (spaceId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/media/gallery/${spaceId}`, {
      headers: { ...authHeader() },
    });
    if (!res.ok) throw new Error('Error al obtener galería');
    return await res.json();
  } catch (error) {
    console.error('Error:', error);
    return [];
  }
};

export const uploadGalleryImage = async (spaceId, imageFile, title, description, uploadedBy, onProgress) => {
  try {
    // Validar tamaño antes de subir (máximo 1GB)
    const MAX_SIZE = 1 * 1024 * 1024 * 1024;
    if (imageFile.size > MAX_SIZE) {
      throw new Error('Archivo demasiado grande. Máximo 1GB');
    }

    // Usar FormData para subir archivos físicamente
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('media', imageFile);
      formData.append('title', title || imageFile.name);
      formData.append('description', description || '');
      formData.append('uploadedBy', uploadedBy || '');

      const xhr = new XMLHttpRequest();
      const token = localStorage.getItem('token');

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch {
            resolve({ message: 'Archivo subido correctamente' });
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.error || `Error: ${xhr.status}`));
          } catch {
            reject(new Error('Error al subir archivo'));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Error de conexión al subir'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Subida cancelada'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('Tiempo de subida agotado. Intenta con un archivo más pequeño'));
      });

      xhr.timeout = 900000; // 15 minutos
      xhr.open('POST', `${API_BASE_URL}/api/media/gallery/${spaceId}/upload`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const deleteGalleryImage = async (imageId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/media/gallery/${imageId}`, {
      method: 'DELETE',
      headers: { ...authHeader() }
    });
    if (!res.ok) throw new Error('Error al eliminar imagen');
    return await res.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

// ==================== DOCUMENTOS ====================

export const getSpaceDocuments = async (spaceId, category = null) => {
  try {
    let url = `${API_BASE_URL}/api/media/documents/${spaceId}`;
    if (category) url += `?category=${category}`;
    
    const res = await fetch(url, { headers: { ...authHeader() } });
    if (!res.ok) throw new Error('Error al obtener documentos');
    return await res.json();
  } catch (error) {
    console.error('Error:', error);
    return [];
  }
};

export const uploadDocument = async (spaceId, documentFile, description, uploadedBy, category = 'General', onProgress) => {
  try {
    // Validar tamaño antes de subir (máximo 1GB)
    const MAX_SIZE = 1 * 1024 * 1024 * 1024;
    if (documentFile.size > MAX_SIZE) {
      throw new Error('Archivo demasiado grande. Máximo 1GB');
    }

    // Crear FormData para subir el archivo
    const formData = new FormData();
    formData.append('document', documentFile);
    formData.append('description', description || '');
    formData.append('uploadedBy', uploadedBy);
    formData.append('category', category);

    // Usar XMLHttpRequest para rastrear progreso de upload
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const token = localStorage.getItem('token');

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const data = JSON.parse(xhr.responseText);
          resolve(data);
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.error || `Error: ${xhr.status}`));
          } catch {
            reject(new Error('Error al subir documento'));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Error de conexión al subir'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Subida cancelada'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('Tiempo de subida agotado. Intenta con un archivo más pequeño'));
      });

      xhr.timeout = 900000; // 15 minutos
      xhr.open('POST', `${API_BASE_URL}/api/media/documents/${spaceId}`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const deleteDocument = async (documentId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/media/documents/${documentId}`, {
      method: 'DELETE',
      headers: { ...authHeader() }
    });
    if (!res.ok) throw new Error('Error al eliminar documento');
    return await res.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const getDocumentVersions = async (documentId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/media/documents/${documentId}/versions`, {
      headers: { ...authHeader() },
    });
    if (!res.ok) throw new Error('Error al obtener versiones');
    return await res.json();
  } catch (error) {
    console.error('Error:', error);
    return [];
  }
};

// Funciones utilitarias
export const getFileExtension = (fileName) => {
  return fileName.split('.').pop().toLowerCase();
};

// Se mira también la extensión, no solo el mimetype: al subir desde un
// celular —y a veces desde el propio Windows— el navegador declara
// "application/octet-stream" y un .csv perfectamente normal salía etiquetado
// como "ARC", archivo desconocido.
const ICONO_POR_EXTENSION = {
  pdf: 'PDF',
  doc: 'DOC', docx: 'DOC', odt: 'DOC', rtf: 'DOC',
  xls: 'XLS', xlsx: 'XLS', ods: 'XLS', csv: 'CSV',
  ppt: 'PPT', pptx: 'PPT', odp: 'PPT',
  txt: 'TXT', md: 'TXT', log: 'TXT',
  mp4: 'VID', mov: 'VID', avi: 'VID', webm: 'VID', mkv: 'VID',
  mp3: 'AUD', wav: 'AUD', ogg: 'AUD', m4a: 'AUD',
  jpg: 'IMG', jpeg: 'IMG', png: 'IMG', gif: 'IMG', webp: 'IMG', bmp: 'IMG',
  zip: 'ZIP', rar: 'ZIP', '7z': 'ZIP', gz: 'ZIP',
};

export const getFileIcon = (fileType, fileName = '') => {
  const type = (fileType || '').toLowerCase();
  const ext = (fileName || '').split('.').pop().toLowerCase();

  if (ICONO_POR_EXTENSION[ext]) return ICONO_POR_EXTENSION[ext];

  if (type.includes('pdf')) return 'PDF';
  if (type.includes('word') || type.includes('document')) return 'DOC';
  if (type.includes('csv')) return 'CSV';
  if (type.includes('sheet') || type.includes('excel')) return 'XLS';
  if (type.includes('presentation') || type.includes('powerpoint')) return 'PPT';
  if (type.includes('text')) return 'TXT';
  if (type.includes('video')) return 'VID';
  if (type.includes('audio')) return 'AUD';
  if (type.includes('image')) return 'IMG';
  if (type.includes('zip') || type.includes('rar') || type.includes('compress')) return 'ZIP';
  return 'ARC';
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// ==================== VISUALIZACIÓN DE DOCUMENTOS ====================
//
// Qué documentos se pueden LEER dentro de la plataforma, sin descargarlos.
// El navegador sabe mostrar por sí solo tres cosas: PDF, imágenes y texto
// plano. Word, Excel y PowerPoint no: no existe forma de abrirlos en el
// navegador sin mandarlos a un servicio externo, así que para esos se ofrece
// la descarga y se dice con todas las letras, en vez de abrir un visor vacío.
//
// Se mira el mimetype Y la extensión, porque cuando el archivo se sube desde
// un celular el navegador a veces reporta "application/octet-stream" y el
// mimetype guardado no sirve para decidir.

const EXTENSIONES_VISTA = {
  pdf: ['pdf'],
  imagen: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'],
  texto: ['txt', 'csv', 'md', 'log'],
};

/**
 * Devuelve cómo se puede mostrar el documento: 'pdf', 'imagen', 'texto',
 * o null si solo se puede descargar.
 */
export const tipoDeVista = (fileType, fileName = '') => {
  const tipo = (fileType || '').toLowerCase();
  const ext = (fileName || '').split('.').pop().toLowerCase();

  if (tipo.includes('pdf') || EXTENSIONES_VISTA.pdf.includes(ext)) return 'pdf';
  if (tipo.startsWith('image/') || EXTENSIONES_VISTA.imagen.includes(ext)) return 'imagen';
  // Ojo con el orden: 'text/csv' empieza por 'text/', pero también hay que
  // aceptar el csv que llega declarado como hoja de cálculo genérica.
  if (tipo.startsWith('text/') || EXTENSIONES_VISTA.texto.includes(ext)) return 'texto';

  return null;
};

/** ¿Se puede leer dentro de la plataforma, sin descargarlo? */
export const canPreviewFile = (fileType, fileName = '') =>
  tipoDeVista(fileType, fileName) !== null;

/** Etiqueta corta para explicar por qué un documento no se puede mostrar. */
export const motivoSinVista = (fileType, fileName = '') => {
  const tipo = (fileType || '').toLowerCase();
  const ext = (fileName || '').split('.').pop().toLowerCase();
  if (tipo.includes('word') || ['doc', 'docx'].includes(ext)) return 'Word';
  if (tipo.includes('sheet') || tipo.includes('excel') || ['xls', 'xlsx'].includes(ext)) return 'Excel';
  if (tipo.includes('presentation') || tipo.includes('powerpoint') || ['ppt', 'pptx'].includes(ext)) return 'PowerPoint';
  return null;
};
